import {
    SearchCustomerInput,
    SearchResultUuidDocument,
    SearchResultUuidQuery,
    useCustomerInformationByUuidQuery,
    useSearchCustomerMutation,
} from 'gql/__generated/vp-apollo';
import { useCallback } from 'react';
import { useErrorHandling } from './useErrorHandling';
import { ApolloCache, InMemoryCache } from '@apollo/client';
import {
    CrmtServiceUnavailableErrorResult,
    InstallationAddressInvalidErrorResult,
    NoContractFoundErrorResult,
    RateLimitExceededErrorResult,
} from '@cobra/fe-lib/client-error-handling/classes/ErrorResult';

const useSearchCustomer = () => {
    const { error, handleError, resetError } = useErrorHandling<
        | NoContractFoundErrorResult
        | RateLimitExceededErrorResult
        | CrmtServiceUnavailableErrorResult
        | InstallationAddressInvalidErrorResult
    >();

    const [mutate, { data, loading }] = useSearchCustomerMutation({
        onError(error) {
            handleError(error);
        },
    });

    const storeUuid = useCallback((cache: ApolloCache<InMemoryCache>, uuid: string) => {
        cache.writeQuery<SearchResultUuidQuery>({
            query: SearchResultUuidDocument,
            data: {
                searchResultUuid: uuid,
            },
        });
    }, []);

    const searchCustomer = useCallback(
        async (customerData: SearchCustomerInput) => {
            resetError();
            return mutate({
                variables: { input: customerData },
                update(cache, { data }) {
                    if (data) {
                        storeUuid(cache, data.searchCustomer.uuid);
                    }
                },
            });
        },
        [mutate, resetError, storeUuid]
    );

    return {
        searchCustomer,
        uuid: data?.searchCustomer.uuid || null,
        loading,
        error,
    };
};

const useCustomerInformationByUuid = (uuid: string | null) => {
    const { error, handleError } = useErrorHandling<
        | NoContractFoundErrorResult
        | RateLimitExceededErrorResult
        | CrmtServiceUnavailableErrorResult
        | InstallationAddressInvalidErrorResult
    >();

    const { data, loading } = useCustomerInformationByUuidQuery({
        skip: typeof uuid !== 'string',
        onError(error) {
            handleError(error);
        },
        variables: {
            uuid: uuid as string,
        },
    });

    return {
        installationAddress: data?.customerInformation.contracts.products.dslOrder.installationAddress || null,
        loading,
        error,
    };
};

export const useSearchAndResolveCustomer = () => {
    const { searchCustomer, uuid, error: searchError, loading: searching } = useSearchCustomer();
    const { installationAddress, error: resolveError, loading: resolving } = useCustomerInformationByUuid(uuid);

    return {
        installationAddress,
        searchAndResolveCustomer: searchCustomer,
        error: searchError || resolveError,
        loading: searching || resolving,
    };
};
